import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Stack } from "@altimateai/lego";
import { ChatTriggerLink } from "@altimateai/ui-components/chatbotV2";
import { useGlobalPlaygroundActions, PlaygroundUserViewEntities } from "@/modules/playground";
import type { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import { getInitialQuestions } from "../api";

interface UseAskAIOptions {
  resourceType: string;
  resourceId: string;
  resourceName: string | undefined;
  entityType: PlaygroundUserViewEntities;
  context?: Record<string, unknown>;
}

const buildOpportunityMessage = (
  opportunity: GetOpportunitiesDataResponse,
  workspaceName?: string
) => {
  const resourceNames =
    opportunity.resources
      ?.map(r => r.resource_name)
      .filter(Boolean)
      .join(", ") || "this resource";
  const workspaceInfo = workspaceName ? `\n\n**Workspace:** ${workspaceName}` : "";
  return `Help me solve this opportunity:

**Opportunity:** ${opportunity.opportunity_name}

**Description:** ${opportunity.description || "No description available"}

**Resource:** ${resourceNames}${workspaceInfo}

What specific steps should I take to implement this optimization and realize the potential savings?`;
};

export const useAskAI = ({
  resourceType,
  resourceId,
  resourceName,
  entityType,
  context,
}: UseAskAIOptions) => {
  const { openPlayground } = useGlobalPlaygroundActions();

  const { data } = useQuery({
    queryKey: ["databricks-initial-questions", resourceType, resourceId, resourceName],
    queryFn: () =>
      getInitialQuestions({
        resource_type: resourceType,
        resource_id: resourceId,
        resource_name: resourceName!,
      }),
    enabled: Boolean(resourceId) && Boolean(resourceName),
  });

  const helloMessage: ReactNode | undefined = useMemo(() => {
    if (!data?.questions) return undefined;
    return (
      <div>
        <p className="al-bg-bg-brand al-p-4 al-mb-4">
          I can help you analyze {resourceType} &ldquo;{resourceName}&rdquo;. Here are some
          questions you might find useful:
        </p>
        <Stack direction="column">
          {data.questions.map(question => (
            <ChatTriggerLink key={question} text={question} />
          ))}
        </Stack>
      </div>
    );
  }, [data?.questions, resourceType, resourceName]);

  // When an opportunity is supplied, Studio opens pre-loaded with that opportunity's
  // id and details so the assistant has the full context to act on. Without one, it
  // falls back to resource-level starter questions. Guard against callers that wire this
  // straight to onClick and hand us a React event instead of an opportunity.
  const handleAskAI = (maybeOpportunity?: GetOpportunitiesDataResponse) => {
    const opportunity =
      maybeOpportunity && "opportunity_id" in maybeOpportunity ? maybeOpportunity : undefined;
    const opportunityContext = opportunity
      ? {
          opportunity_id: opportunity.opportunity_id,
          opportunity_name: opportunity.opportunity_name,
          description: opportunity.description,
          resources: opportunity.resources,
        }
      : {};
    openPlayground({
      chatbotProps: opportunity
        ? {
            initialMessage: buildOpportunityMessage(opportunity, context?.workspace_name as string),
          }
        : { initialMessage: "", helloMessage },
      context: {
        entity_type: entityType,
        ...context,
        ...opportunityContext,
      },
    });
  };

  return { handleAskAI };
};
